import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { FjCheckbox } from 'components/primitives';
import { Label } from 'components/styled-primitives';

import { selectActiveFilters } from 'selectors/super-admin';
import { SuperAdminActions } from 'actions';
import {
  STATUS_ACTIVE,
  STATUS_INACTIVE,
  PENDING,
  ACTIVE,
  CANCELLED,
  SUSPENDED,
  RELEASE,
  TRAINING,
  BETA,
  TRIAL,
  EMAIL_VERIFIED,
  EMAIL_UNVERIFIED,
  PHANTOM_USER,
  REGISTERED_USER,
  USER_ACTIVE,
  USER_INACTIVE,
  licensedProductRadios,
  countryFilters,
} from 'constants/filters';

// Minimal shape of a filter option accessed by this component.
interface FilterOption {
  id: string;
  label: React.ReactNode;
}

interface FilterSelectionToolbarProps {
  numberOfResults?: number;
  hideLicenseFilters?: boolean;
  hideProductFilters?: boolean;
  showUserFilters?: boolean;
  hideStatusFilters?: boolean;
}

const FilterSelectionToolbar = ({
  numberOfResults = 0,
  hideLicenseFilters = false,
  hideProductFilters = false,
  showUserFilters = false,
  hideStatusFilters,
}: FilterSelectionToolbarProps) => {
  // `selectActiveFilters` resolves a dynamic lodash path and is typed `unknown`;
  // it holds a map of filter-id -> active flag.
  const activeFilters = useAppSelector(selectActiveFilters) as Record<string, boolean>;
  const dispatch = useAppDispatch();
  const toggleFilter = (id: string) => dispatch(SuperAdminActions.toggleFilter(id));

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Close the open dropdown on an outside click (was componentDidMount/WillUnmount).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && dropdownRefs.current[openDropdown]) {
        if (!dropdownRefs.current[openDropdown]?.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const toggleDropdown = (dropdownName: string) => {
    setOpenDropdown((prev) => (prev === dropdownName ? null : dropdownName));
  };

  const isFilterActive = (filter: string): boolean => !!activeFilters[filter];

  const getActiveCount = (optionsGroup: FilterOption[]): number =>
    optionsGroup.filter((option) => isFilterActive(option.id)).length;

  const renderFilterDropdown = (
    optionsGroup: FilterOption[],
    title: string,
    dropdownKey: string,
    checkboxType: string = 'checkbox',
  ): React.JSX.Element => {
    const isOpen = openDropdown === dropdownKey;
    const activeCount = getActiveCount(optionsGroup);
    const totalCount = optionsGroup.length;

    return (
      <DropdownContainer
        key={dropdownKey}
        ref={(ref) => {
          dropdownRefs.current[dropdownKey] = ref;
        }}
      >
        <DropdownButton onClick={() => toggleDropdown(dropdownKey)}>
          <Label>{title}</Label>
          <CountBadge>
            {activeCount}/{totalCount}
          </CountBadge>
          <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
        </DropdownButton>
        {isOpen && (
          <DropdownMenu>
            {optionsGroup.map((option) => (
              <DropdownItem key={option.id}>
                <Checkbox
                  type={checkboxType}
                  label={<FilterLabel>{option.label}</FilterLabel>}
                  checked={isFilterActive(option.id)}
                  onChange={() => toggleFilter(option.id)}
                  bgColor="white"
                  data-testid={`${option.id.toLowerCase()}-filter-${checkboxType}`}
                />
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </DropdownContainer>
    );
  };

  // License Filters
  const renderLicenseFilters = () => (
    <>
      {!hideStatusFilters &&
        renderFilterDropdown([STATUS_ACTIVE, STATUS_INACTIVE], 'Status', 'status')}
      {!hideProductFilters && renderFilterDropdown(licensedProductRadios(), 'Products', 'products')}
      {!hideLicenseFilters &&
        renderFilterDropdown([BETA, RELEASE, TRIAL, TRAINING], 'License Type', 'licenseType')}
      {!hideStatusFilters &&
        renderFilterDropdown(
          [PENDING, ACTIVE, CANCELLED, SUSPENDED],
          'Activity State',
          'activityState',
        )}
      {renderFilterDropdown(countryFilters(), 'Country', 'country')}
    </>
  );

  // User Filters
  const renderUserFilters = () => (
    <>
      {renderFilterDropdown(
        [EMAIL_UNVERIFIED, EMAIL_VERIFIED],
        'Email Verification',
        'emailVerification',
        'radio',
      )}
      {renderFilterDropdown([REGISTERED_USER, PHANTOM_USER], 'User Type', 'userType', 'radio')}
      {renderFilterDropdown(
        [USER_INACTIVE, USER_ACTIVE],
        'Account Status',
        'accountStatus',
        'radio',
      )}
      {renderFilterDropdown(countryFilters(), 'Country', 'country')}
    </>
  );

  return (
    <ToolbarContainer>
      {showUserFilters ? renderUserFilters() : renderLicenseFilters()}
      <ResultsSection>
        <span>{numberOfResults} matching</span>
        {/* A license row is one flattened productAccess entry, not one cost center, so the count
            is labelled "licenses"; on the Users page a row is a user record. */}
        <span>{showUserFilters ? 'records found' : 'licenses found'}</span>
      </ResultsSection>
    </ToolbarContainer>
  );
};

export default FilterSelectionToolbar;

const ToolbarContainer = styled.div`
  display: flex;
  /* Wrap rather than overflow: the filters and the result count are fixed-width
     content in a row that cannot shrink, so on a narrow window they pushed the
     whole page wider than the viewport instead of giving way. Height is auto
     (with min-height as the floor) so a wrapped second line shows rather than
     being clipped. */
  flex-wrap: wrap;
  min-width: 0;
  background-color: ${({ theme }) => theme.colors.tabBackground};
  padding: 5px 0px;
  height: auto;
  min-height: 60px;
  gap: ${({ theme }) => theme.grid.units(0.5)};
`;

const Checkbox = styled(FjCheckbox)`
  margin: 0;
`;

const FilterLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
`;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0px 20px;
  border-right: 2px solid ${({ theme }) => theme.colors.notSelectable};
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 14px;
  min-width: 150px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.selectableLightBg};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.selectableLightBg};
    outline-offset: 2px;
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.selectableLightBg};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  margin-left: auto;
`;

const DropdownArrow = styled.span<{ isOpen: boolean }>`
  font-size: 10px;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  color: ${({ theme }) => theme.colors.selectableLightBg};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 20px;
  margin-top: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.selectableLightBg};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  /* Above FjTooltip's popup (.fj-tooltip-portal .visor, z-index: 1001) - this dropdown can be
     open at the same time as a tooltip on InstitutionalLicenseTable's "Allocated Seats" column,
     and should render on top of it, matching its pre-existing relative stacking order. */
  z-index: 1002;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.selected};
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /* Allows the count to shrink instead of forcing the toolbar wider. */
  min-width: 0;
  padding: 0px 20px;
  font-size: 14px;
  /* productTitle (maroon) is branded title text and reads as an error color for a neutral
     result count; black matches license-search-controls' StatusText on the same background. */
  color: ${({ theme }) => theme.colors.black};
  margin-left: auto;
`;
